import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";

import { CHART_TYPES } from "./constants";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AnalysisType =
  | "descriptive"
  | "histogram"
  | "boxplot"
  | "scatter"
  | "fft"
  | "polyFit"
  | "line"
  | "bar"
  | "heatmap"
  | "surface"
  | "scatter3d"
  | "correlation"
  | "contour";

export type ChartTheme = "dark" | "light";

export type DescriptiveResult = {
  count: number;
  mean: number;
  median: number;
  std: number;
  variance: number;
  min: number;
  max: number;
  q1: number;
  q3: number;
  iqr: number;
};

export type ChartOptions = {
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  showGrid: boolean;
  polyDegree: number;
  histogramBins: number; // 0 = auto
};

export const DEFAULT_CHART_OPTIONS: ChartOptions = {
  title: "",
  xAxisLabel: "",
  yAxisLabel: "",
  showGrid: true,
  polyDegree: 3,
  histogramBins: 0,
};

export type ChartResult = {
  plotlyData: Record<string, unknown>[];
  plotlyLayout: Record<string, unknown>;
  chartType: string;
};

type ChartAnalysisType = Exclude<AnalysisType, "descriptive">;

export type AnalysisResult =
  | { type: "descriptive"; data: DescriptiveResult }
  | { type: ChartAnalysisType; data: ChartResult };

export function isChartResult(
  result: AnalysisResult,
): result is Extract<AnalysisResult, { data: ChartResult }> {
  return CHART_TYPES.has(result.type);
}

/** Patches plotlyLayout for cosmetic options only, so no re-computation is needed. */
export function applyChartOptions(chartResult: ChartResult, options: ChartOptions): ChartResult {
  const layout = { ...chartResult.plotlyLayout };

  if (options.title) {
    layout.title = { text: options.title, font: { size: 14 } };
  }
  const xaxis: Record<string, unknown> | undefined = layout.xaxis
    ? { ...(layout.xaxis as Record<string, unknown>) }
    : undefined;
  const yaxis: Record<string, unknown> | undefined = layout.yaxis
    ? { ...(layout.yaxis as Record<string, unknown>) }
    : undefined;

  if (options.xAxisLabel && xaxis) {
    xaxis.title = { text: options.xAxisLabel };
  }
  if (options.yAxisLabel && yaxis) {
    yaxis.title = { text: options.yAxisLabel };
  }
  if (xaxis) xaxis.showgrid = options.showGrid;
  if (yaxis) yaxis.showgrid = options.showGrid;

  // datarevision must change per options, else Plotly reuses a stale axis range.
  const datarevision = JSON.stringify(options);

  return {
    ...chartResult,
    plotlyLayout: { ...layout, xaxis, yaxis, datarevision },
  };
}

// ---------------------------------------------------------------------------
// Data source detection
// ---------------------------------------------------------------------------

/** A multi-cell matrix, or a single-cell group of comma-separated numbers like [1, 4, 7]. */
export type DataSource =
  | { kind: "matrix"; node: MatrixNodeModel }
  | { kind: "group"; node: GroupNodeModel }
  | { kind: "tiptap-table"; node: ProsemirrorNode; pos: number; doc: DocumentModel };

export function getActiveDataSource(doc: DocumentModel): DataSource | null {
  const cursorContext = doc.cursorContext();
  const { cursor, selection } = cursorContext;

  if (selection.hasSelectedNodes) {
    for (const node of selection.selectedNodes) {
      const source = findDataSource(node);
      if (source) return source;
    }
    return null;
  }

  let current: NodeModel | undefined = cursor.node;
  while (current) {
    const source = findDataSource(current);
    if (source) return source;
    current = current.parent;
  }

  // Tiptap Table Case
  // We access editorState.isTable here strictly to trigger MobX to observe it.
  // This causes components like DataAnalysisPanel to re-render when a table is selected.
  if (doc.editor.editorState?.isTable) {
    const tiptapSource = findTiptapTableDataSource(doc);
    if (tiptapSource) return tiptapSource;
  }

  return null;
}

function findDataSource(node: NodeModel): DataSource | null {
  if (node.is(GroupNodeModel)) {
    const { matrixNode } = node;
    if (!matrixNode) return null;

    if (matrixNode.rows > 1 || matrixNode.cols > 1) {
      if (countNumericCells(matrixNode) >= 2) {
        return { kind: "matrix", node: matrixNode };
      }
      return null;
    }

    // 1×1 matrix = single inline cell with comma-separated numbers
    if (countNumericNodesInGroup(node) >= 2) {
      return { kind: "group", node };
    }
    return null;
  }

  if (node.is(MatrixNodeModel) && (node.rows > 1 || node.cols > 1)) {
    if (countNumericCells(node) >= 2) {
      return { kind: "matrix", node };
    }
  }

  return null;
}

function findTiptapTableDataSource(doc: DocumentModel): DataSource | null {
  const { tiptap } = doc.editor;
  if (!tiptap) return null;

  const { state } = tiptap;
  const { selection } = state;
  const { $anchor } = selection;

  const { depth: maxDepth } = $anchor;
  for (let depth = maxDepth; depth > 0; depth -= 1) {
    const node = $anchor.node(depth);
    if (node.type.name === "table") {
      if (countNumericTiptapCells(node, doc) >= 2) {
        return { kind: "tiptap-table", node, pos: $anchor.before(depth), doc };
      }
    }
  }

  return null;
}

/** Handles both raw text cells and inlineExpressions. */
function extractNumberFromCell(cellNode: ProsemirrorNode, doc: DocumentModel): number | null {
  const text = cellNode.textContent.trim();
  if (text !== "") {
    const val = Number(text);
    if (!isNaN(val)) return val;
  }

  let val: number | null = null;
  let hasComplexExpression = false;

  cellNode.descendants((node) => {
    if (node.type.name === "inlineExpression") {
      const wrapperNodeId = node.attrs.nodeId as string;
      const wrapperNode = doc.textNode.wrapperNodes.find((n) => n.id === wrapperNodeId);
      const inlineNode = wrapperNode?.inlineNode;
      if (inlineNode && inlineNode.nodes.length === 1 && inlineNode.nodes[0].is(NumberNodeModel)) {
        val = Number(inlineNode.nodes[0].props.value);
      } else {
        hasComplexExpression = true;
      }
    }
  });

  if (hasComplexExpression) return null;
  if (val !== null && !isNaN(val)) return val;

  return null;
}

function countNumericTiptapCells(tableNode: ProsemirrorNode, doc: DocumentModel): number {
  let count = 0;
  tableNode.descendants((node) => {
    if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
      const val = extractNumberFromCell(node, doc);
      if (val !== null) {
        count += 1;
      }
    }
  });
  return count;
}

function countNumericCells(matrixNode: MatrixNodeModel): number {
  return extractFromMatrix(matrixNode).length;
}

function countNumericNodesInGroup(group: GroupNodeModel): number {
  return extractFromGroup(group).length;
}

// ---------------------------------------------------------------------------
// Data extraction
// ---------------------------------------------------------------------------

export function extractNumericData(source: DataSource): number[] {
  switch (source.kind) {
    case "matrix":
      return extractFromMatrix(source.node);
    case "group":
      return extractFromGroup(source.node);
    case "tiptap-table":
      return extractFromTiptapTable(source.node, source.doc);
  }
}

function extractFromMatrix(matrixNode: MatrixNodeModel): number[] {
  const values: number[] = [];
  for (const cell of matrixNode.matrixNodes) {
    if (cell.nodes.length === 1 && cell.nodes[0].is(NumberNodeModel)) {
      const val = parseFloat(cell.nodes[0].props.value);
      if (!isNaN(val)) {
        values.push(val);
      }
    }
  }
  return values;
}

function extractFromGroup(group: GroupNodeModel): number[] {
  const inlineNode = group.matrixNode?.matrixNodes[0];
  if (!inlineNode) return [];

  const values: number[] = [];
  for (const child of inlineNode.nodes) {
    if (child.is(NumberNodeModel)) {
      const val = parseFloat(child.props.value);
      if (!isNaN(val)) {
        values.push(val);
      }
    }
  }
  return values;
}

function extractFromTiptapTable(tableNode: ProsemirrorNode, doc: DocumentModel): number[] {
  const values: number[] = [];
  tableNode.descendants((node) => {
    if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
      const val = extractNumberFromCell(node, doc);
      if (val !== null) {
        values.push(val);
      }
    }
  });
  return values;
}

/** Returns null if any cell is non-numeric, which gates heatmap availability. */
export function extractNumericData2D(matrixNode: MatrixNodeModel): number[][] | null {
  const { rows, cols, matrixNodes } = matrixNode;
  const result: number[][] = [];

  for (let r = 0; r < rows; r += 1) {
    const row: number[] = [];
    for (let c = 0; c < cols; c += 1) {
      const cell = matrixNodes[r * cols + c];
      if (!cell || cell.nodes.length !== 1 || !cell.nodes[0].is(NumberNodeModel)) {
        return null;
      }
      const val = parseFloat(cell.nodes[0].props.value);
      if (isNaN(val)) return null;
      row.push(val);
    }
    result.push(row);
  }

  return result;
}

export function extractNumericData2DFromTiptap(
  tableNode: ProsemirrorNode,
  doc: DocumentModel,
): number[][] | null {
  const result: number[][] = [];

  tableNode.forEach((rowNode) => {
    if (rowNode.type.name === "tableRow") {
      const rowVars: number[] = [];

      rowNode.forEach((cellNode) => {
        if (cellNode.type.name === "tableCell" || cellNode.type.name === "tableHeader") {
          const val = extractNumberFromCell(cellNode, doc);
          if (val !== null) {
            rowVars.push(val);
          }
        }
      });
      if (rowVars.length > 0) {
        result.push(rowVars);
      }
    }
  });

  // A strict 2D matrix needs equal-length rows and more than one row and column.
  if (result.length > 0) {
    const colCount = result[0].length;
    for (let r = 1; r < result.length; r += 1) {
      if (result[r].length !== colCount) return null;
    }
    if (result.length <= 1 || colCount <= 1) return null;
  } else {
    return null;
  }

  return result;
}

export function isAllNumericMatrix(source: DataSource): boolean {
  if (source.kind === "matrix") {
    if (source.node.rows <= 1 || source.node.cols <= 1) return false;
    return extractNumericData2D(source.node) !== null;
  }
  if (source.kind === "tiptap-table") {
    return extractNumericData2DFromTiptap(source.node, source.doc) !== null;
  }
  return false;
}
